/**
 * IDDLInbox — Phase 1.5 candidate-facing simulation primitive.
 *
 * A Gmail-shaped inbox that emits the 16 canonical inbox events to
 * /api/attempts/{attemptId}/events on every meaningful action.
 *
 * Behavioural fidelity > visual polish:
 *  • clicks emit real events with `client_ts_ms` so the server can
 *    compute dwell / hesitation / interaction-count / reversal flags
 *  • inspecting the sender / header / hovering links are all explicit
 *    actions that require commitment (small click + dropdown), not
 *    silent CSS hover; this prevents accidental telemetry
 *  • the report / delete / archive / reply / forward actions are real
 *    DOM clicks; no "are you sure" dialog so the candidate's first
 *    instinct is what gets logged
 *  • the "Send" action on a draft is what flips the unsafe-action
 *    auto-fail; closing the compose pane without sending is safe
 *
 * Props:
 *   attemptId      : str
 *   conceptId      : str   (e.g. "CS-TA-03")
 *   scenarioText   : { inbox_name, messages: [ {id, is_phishing, …} ] }
 *   onFinalize?    : ({decision, messageId}) => void  (parent decides if
 *                    finalize closes the sim; the event is ALWAYS emitted
 *                    to the backend first)
 *   onEventLogged? : (eventDoc) => void  // optional live-log hook
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Inbox, Mail, ShieldAlert, Trash2, Archive, Reply, Forward, Paperclip, Eye, AlertTriangle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

// Iter-25.2 — the candidate's per-email triage commitment is captured
// SOLELY via the toolbar actions (Report · Archive · Delete · Reply ·
// Forward). The legacy bottom "final decision" widget has been
// removed. The toolbar IS the single action surface.
//
// Verdict mapping (used for the per-row chip label):
//   message_reported  → "Reported"
//   message_archived  → "Archived"
//   message_deleted   → "Deleted"
//   reply_drafted     → "Replied"
//   forward_drafted   → "Forwarded"
// Anything else (just `message_opened`) = "Pending" → blocks Continue.
const TOOLBAR_ACTION_LABEL_KEY = {
  message_reported: "sim.inbox.row_reported",
  message_archived: "sim.inbox.row_archived",
  message_deleted:  "sim.inbox.row_deleted",
  reply_drafted:    "sim.inbox.row_replied",
  reply_sent:       "sim.inbox.row_replied",
  forward_drafted:  "sim.inbox.row_forwarded",
  forward_sent:     "sim.inbox.row_forwarded",
};

export default function IDDLInbox({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  const [openedId, setOpenedId] = useState(null);
  const [inspecting, setInspecting] = useState({ sender: false, header: false, link: null });
  const [compose, setCompose] = useState(null); // { kind: "reply"|"forward", messageId, body }
  const [finalized, setFinalized] = useState(null);
  const inboxFiredRef = useRef(false);
  // Iter-25.2 — per-message triage state. `lastActionByMsgId` records
  // the MOST RECENT triage-qualifying toolbar action per message_id so
  // the per-row chip can show the action label. `triagedSet` is
  // derived from this map's keys. Just opening / inspecting an email
  // is NOT enough — the candidate must take a deliberate toolbar
  // action (Report / Archive / Delete / Reply / Forward).
  const [lastActionByMsgId, setLastActionByMsgId] = useState(() => ({}));

  const messages = scenarioText?.messages || [];
  const openedMsg = useMemo(
    () => messages.find((m) => m.id === openedId) || null,
    [messages, openedId],
  );

  // Fire the canonical `inbox_opened` event the first time the inbox
  // mounts — required event #1 for every inbox concept.
  useEffect(() => {
    if (inboxFiredRef.current) return;
    inboxFiredRef.current = true;
    emit("inbox_opened", null, "inbox_list");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      // Iter-25.2 — record per-message triage. Only TOOLBAR ACTIONS
      // count (Report / Archive / Delete / Reply-draft /
      // Forward-draft). `message_opened` / `sender_inspected` /
      // `header_inspected` / `link_hovered` are inspection signals
      // and do NOT satisfy the per-email triage gate.
      const mid = eventValue && typeof eventValue === "object" ? eventValue.message_id : null;
      if (mid && TOOLBAR_ACTION_LABEL_KEY[eventName]) {
        setLastActionByMsgId((prev) => ({ ...prev, [mid]: eventName }));
      }
      return r?.event;
    } catch (e) {
      // 409 means the engine flag is off — show a clear error rather than
      // failing silently. 4xx/5xx errors surface a toast so research
      // sessions don't proceed on a broken pipe.
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} failed: ${detail}`);
      return null;
    }
  }

  const open = (m) => {
    setOpenedId(m.id);
    setInspecting({ sender: false, header: false, link: null });
    emit("message_opened", { message_id: m.id, subject: m.subject }, "message_view");
  };

  const inspectSender = () => {
    if (!openedMsg) return;
    setInspecting((s) => ({ ...s, sender: true }));
    emit(
      "sender_inspected",
      {
        message_id: openedMsg.id,
        from_display: openedMsg.from_display,
        from_email: openedMsg.from_email,
        real_domain: openedMsg.from_real_domain,
      },
      "message_view",
    );
  };

  const inspectHeader = () => {
    if (!openedMsg) return;
    setInspecting((s) => ({ ...s, header: true }));
    emit(
      "header_inspected",
      { message_id: openedMsg.id, red_flags: openedMsg.header_red_flags || [] },
      "message_view",
    );
  };

  const hoverLink = (link) => {
    setInspecting((s) => ({ ...s, link: link.id }));
    emit(
      "link_hovered",
      { message_id: openedMsg.id, link_id: link.id, display_url: link.display_url, real_url: link.real_url },
      "message_view",
    );
  };

  const clickLink = (link) => {
    emit(
      "link_clicked",
      { message_id: openedMsg.id, link_id: link.id, real_url: link.real_url },
      "message_view",
    );
    // No navigation — sandboxed.
  };

  const openAttachment = (att) => {
    emit(
      "attachment_opened",
      { message_id: openedMsg.id, attachment_id: att.id, filename: att.filename, mime: att.mime },
      "message_view",
    );
  };
  const saveAttachment = (att) => {
    emit(
      "attachment_saved",
      { message_id: openedMsg.id, attachment_id: att.id, filename: att.filename },
      "message_view",
    );
  };
  const reportMessage = () => {
    if (!openedMsg) return;
    emit("message_reported", { message_id: openedMsg.id }, "message_view");
    toast.success(t("sim.inbox.reported_toast"));
  };
  const deleteMessage = () => {
    if (!openedMsg) return;
    emit("message_deleted", { message_id: openedMsg.id }, "message_view");
  };
  const archiveMessage = () => {
    if (!openedMsg) return;
    emit("message_archived", { message_id: openedMsg.id }, "message_view");
  };
  const startReply = () => {
    if (!openedMsg) return;
    setCompose({ kind: "reply", messageId: openedMsg.id, body: "" });
    emit("reply_drafted", { message_id: openedMsg.id }, "compose");
  };
  const startForward = () => {
    if (!openedMsg) return;
    setCompose({ kind: "forward", messageId: openedMsg.id, body: "" });
    emit("forward_drafted", { message_id: openedMsg.id }, "compose");
  };
  const sendCompose = () => {
    if (!compose) return;
    const evName = compose.kind === "reply" ? "reply_sent" : "forward_sent";
    emit(evName, { message_id: compose.messageId, body: compose.body?.slice(0, 200) }, "compose");
    setCompose(null);
  };
  // Iter-25 — `finalize` is retained as a no-op-friendly helper for
  // any future per-email decision strip. The primary submission path
  // is now `completeTriage` below.
  // eslint-disable-next-line no-unused-vars
  const finalize = async (decision) => {
    await emit(
      "final_decision",
      { decision, message_id: openedMsg?.id || null },
      "inbox_list",
    );
    setFinalized(decision);
    if (onFinalize) onFinalize({ decision, messageId: openedMsg?.id || null });
  };
  // Iter-25.2 — holistic inbox-completion gate. The candidate cannot
  // advance to reflection until every message has a DELIBERATE
  // toolbar action recorded (just opening + reading is NOT enough).
  // `lastActionByMsgId` is the single source of truth — see the
  // emit() side-effect above.
  const totalMessages = messages.length;
  const triagedIds = Object.keys(lastActionByMsgId);
  const allTriaged = totalMessages > 0 && triagedIds.length >= totalMessages;
  const completeTriage = async () => {
    if (!allTriaged || finalized) return;
    await emit(
      "final_decision",
      {
        decision: "inbox_triaged",
        actions_per_message: lastActionByMsgId,
        triaged_message_ids: triagedIds,
      },
      "inbox_list",
    );
    setFinalized("inbox_triaged");
    toast.success(t("sim.inbox.triaged_toast"));
    if (onFinalize) onFinalize({ decision: "inbox_triaged", messageId: null });
  };

  return (
    <div className="border border-[var(--iddl-rule)] grid grid-cols-12 min-h-[560px]" data-testid="iddl-inbox">
      {/* Iter-25.2 — instruction banner across the top. Makes the
          triage-on-every-email expectation explicit BEFORE the
          candidate starts clicking. */}
      <div
        className="col-span-12 border-b border-[var(--iddl-rule)] bg-[var(--iddl-paper)] px-3 py-2 text-xs"
        data-testid="iddl-inbox-instruction-banner"
      >
        {t("sim.inbox.banner_instruction")}
      </div>
      {/* Sidebar */}
      <aside className="col-span-3 border-r border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3">
        <div className="flex items-center gap-2 mb-3">
          <Inbox size={16} />
          <span className="font-display font-semibold text-sm">Inbox</span>
        </div>
        <div className="label-mono mb-3 truncate">{scenarioText?.inbox_name}</div>
        <button
          type="button"
          className="w-full text-left text-sm bg-[var(--iddl-ink)] text-white px-3 py-2"
          data-testid="iddl-inbox-folder-inbox"
        >
          {t("sim.inbox.folder_inbox", { n: messages.length })}
        </button>
      </aside>

      {/* Message list */}
      <section className="col-span-4 border-r border-[var(--iddl-rule)] overflow-y-auto" data-testid="iddl-inbox-list">
        {messages.map((m) => {
          const action = lastActionByMsgId[m.id] || null;
          const chipKey = action ? TOOLBAR_ACTION_LABEL_KEY[action] : null;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => open(m)}
              className={`w-full text-left p-3 border-b border-[var(--iddl-rule)] hover:bg-[var(--iddl-paper)] ${openedId === m.id ? "bg-[var(--iddl-paper)]" : ""}`}
              data-testid={`iddl-message-row-${m.id}`}
            >
              <div className="text-sm font-semibold truncate flex items-center gap-2">
                <Mail size={12} className="shrink-0" />
                <span className="flex-1 truncate">{m.from_display}</span>
                <span
                  className={`label-mono shrink-0 px-1.5 py-0.5 border border-[var(--iddl-rule)] ${action ? "text-[var(--iddl-ink)]" : "text-[var(--iddl-muted)]"}`}
                  data-testid={`iddl-message-status-${m.id}`}
                  title={action ? t(chipKey) : t("sim.inbox.row_pending")}
                >
                  {action ? t(chipKey) : t("sim.inbox.row_pending")}
                </span>
              </div>
              <div className="text-sm truncate">{m.subject}</div>
              <div className="label-mono truncate">{m.from_email}</div>
            </button>
          );
        })}
      </section>

      {/* Reading pane */}
      <section className="col-span-5 p-4" data-testid="iddl-inbox-reading-pane">
        {!openedMsg && (
          <div className="text-sm text-[var(--iddl-muted)]">
            {t("sim.inbox.select_a_message")}
          </div>
        )}

        {openedMsg && (
          <div className="space-y-3">
            <h3 className="font-display text-lg font-bold leading-tight" data-testid="iddl-message-subject">
              {openedMsg.subject}
            </h3>

            <div className="flex items-center gap-2 flex-wrap text-sm">
              <span data-testid="iddl-sender-display">{openedMsg.from_display}</span>
              <button
                type="button"
                onClick={inspectSender}
                className="label-mono border border-[var(--iddl-rule)] px-2 py-1 hover:bg-[var(--iddl-paper)]"
                data-testid="iddl-btn-inspect-sender"
              >
                <Eye size={12} className="inline mr-1" />{t("sim.inbox.inspect_sender")}
              </button>
              <button
                type="button"
                onClick={inspectHeader}
                className="label-mono border border-[var(--iddl-rule)] px-2 py-1 hover:bg-[var(--iddl-paper)]"
                data-testid="iddl-btn-inspect-header"
              >
                <Eye size={12} className="inline mr-1" />{t("sim.inbox.inspect_headers")}
              </button>
            </div>

            {inspecting.sender && (
              <div className="text-xs border border-amber-400 bg-amber-50 p-2" data-testid="iddl-sender-detail">
                <div><strong>{t("sim.inbox.email_label")}</strong> <code>{openedMsg.from_email}</code></div>
                <div><strong>{t("sim.inbox.real_domain_label")}</strong> <code>{openedMsg.from_real_domain}</code></div>
              </div>
            )}
            {inspecting.header && (
              <div className="text-xs border border-amber-400 bg-amber-50 p-2" data-testid="iddl-header-detail">
                <div className="font-semibold mb-1">{t("sim.inbox.headers_red_flags")}</div>
                <ul className="list-disc pl-4">
                  {(openedMsg.header_red_flags || []).map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                  {(!openedMsg.header_red_flags || !openedMsg.header_red_flags.length) && (
                    <li>{t("sim.inbox.no_anomalies")}</li>
                  )}
                </ul>
              </div>
            )}

            <pre className="text-sm whitespace-pre-wrap font-sans border border-[var(--iddl-rule)] p-3 bg-white" data-testid="iddl-message-body">
              {openedMsg.body}
            </pre>

            {(openedMsg.links || []).length > 0 && (
              <div className="space-y-2" data-testid="iddl-message-links">
                <div className="label-mono">{t("sim.inbox.links_in_message")}</div>
                <div className="text-xs text-[var(--iddl-muted)] italic">
                  {t("sim.inbox.links_help")}
                </div>
                {(openedMsg.links || []).map((lnk) => (
                  <div key={lnk.id} className="flex items-center gap-2 text-sm flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none h-8 text-xs"
                      onMouseEnter={() => hoverLink(lnk)}
                      onClick={() => clickLink(lnk)}
                      data-testid={`iddl-link-${lnk.id}`}
                    >
                      {lnk.display_text || lnk.display_url}
                    </Button>
                    <button
                      type="button"
                      onClick={() => hoverLink(lnk)}
                      className="label-mono border border-[var(--iddl-rule)] px-2 py-1 hover:bg-[var(--iddl-paper)] text-xs"
                      data-testid={`iddl-link-preview-${lnk.id}`}
                      title={t("sim.inbox.preview_url")}
                    >
                      <Eye size={11} className="inline mr-1" />{t("sim.inbox.preview_url")}
                    </button>
                    {inspecting.link === lnk.id && (
                      <code className="text-xs text-rose-700" data-testid={`iddl-link-real-${lnk.id}`}>
                        → {lnk.real_url}
                      </code>
                    )}
                  </div>
                ))}
              </div>
            )}

            {(openedMsg.attachments || []).length > 0 && (
              <div className="space-y-2" data-testid="iddl-message-attachments">
                <div className="label-mono">{t("sim.inbox.attachments")}</div>
                {(openedMsg.attachments || []).map((att) => (
                  <div key={att.id} className="flex items-center gap-2 text-sm">
                    <Paperclip size={14} />
                    <span className="truncate flex-1">{att.filename}</span>
                    <button
                      type="button"
                      onClick={() => openAttachment(att)}
                      className="label-mono border border-rose-300 text-rose-700 px-2 py-1"
                      data-testid={`iddl-att-open-${att.id}`}
                    >
                      {t("sim.inbox.open")}
                    </button>
                    <button
                      type="button"
                      onClick={() => saveAttachment(att)}
                      className="label-mono border border-rose-300 text-rose-700 px-2 py-1"
                      data-testid={`iddl-att-save-${att.id}`}
                    >
                      {t("sim.inbox.save")}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[var(--iddl-rule)]">
              <Button onClick={reportMessage} variant="outline" className="rounded-none" data-testid="iddl-btn-report">
                <ShieldAlert size={14} className="mr-2" />{t("sim.inbox.report")}
              </Button>
              <Button onClick={archiveMessage} variant="outline" className="rounded-none" data-testid="iddl-btn-archive">
                <Archive size={14} className="mr-2" />{t("sim.inbox.archive")}
              </Button>
              <Button onClick={deleteMessage} variant="outline" className="rounded-none" data-testid="iddl-btn-delete">
                <Trash2 size={14} className="mr-2" />{t("sim.inbox.delete")}
              </Button>
              <Button onClick={startReply} variant="outline" className="rounded-none" data-testid="iddl-btn-reply">
                <Reply size={14} className="mr-2" />{t("sim.inbox.reply")}
              </Button>
              <Button onClick={startForward} variant="outline" className="rounded-none" data-testid="iddl-btn-forward">
                <Forward size={14} className="mr-2" />{t("sim.inbox.forward")}
              </Button>
            </div>

            {compose && (
              <div className="mt-3 border border-rose-400 bg-rose-50 p-3" data-testid="iddl-compose-pane">
                <div className="label-mono mb-1">
                  {t("sim.inbox.composing", { kind: compose.kind, to: openedMsg.from_email })}
                </div>
                <textarea
                  value={compose.body}
                  onChange={(e) => setCompose({ ...compose, body: e.target.value })}
                  className="w-full text-sm border border-[var(--iddl-rule)] p-2 min-h-[80px]"
                  placeholder={t("sim.inbox.compose_placeholder")}
                  data-testid="iddl-compose-body"
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={sendCompose}
                    className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="iddl-btn-compose-send"
                  >
                    <Send size={14} className="mr-2" />{t("sim.inbox.btn_send_unsafe")}
                  </Button>
                  <Button
                    onClick={() => setCompose(null)}
                    variant="outline"
                    className="rounded-none"
                    data-testid="iddl-btn-compose-cancel"
                  >
                    {t("sim.inbox.btn_discard")}
                  </Button>
                </div>
                <div className="text-xs text-rose-700 mt-1 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  {t("sim.inbox.send_warning")}
                </div>
              </div>
            )}

          </div>
        )}
      </section>

      {/* Iter-25.2 — bottom continuation bar. Replaces the legacy
          "Final decision" gating block. The button label is ALWAYS
          "Continue to reflection" — flow-progression language; the
          previous "Complete inbox triage" wording was task-language
          and was confusing. Disabled until every message has been
          deliberately triaged (toolbar action). */}
      <div
        className="col-span-12 border-t border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3 flex items-center gap-3 flex-wrap"
        data-testid="iddl-inbox-triage-bar"
      >
        <div className="label-mono" data-testid="iddl-triage-counter">
          {t("sim.inbox.triage_progress", { acted: triagedIds.length, total: totalMessages })}
        </div>
        <div className="flex-1" />
        <Button
          onClick={completeTriage}
          disabled={!allTriaged || !!finalized}
          className="rounded-none btn-ink disabled:opacity-50"
          data-testid="iddl-btn-complete-triage"
        >
          {t("sim.inbox.continue_to_reflection")}
        </Button>
        {!allTriaged && (
          <div className="text-xs text-amber-700" data-testid="iddl-triage-pending">
            {t("sim.inbox.triage_pending_hint", { pending: totalMessages - triagedIds.length })}
          </div>
        )}
      </div>
    </div>
  );
}
